import { useApolloClient } from '@apollo/client';
import type { DocumentNode } from 'graphql';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GET_INSTALLATION_MONITORING_STORES, GET_INSTALLATION_MONITORING_EVENTS } from 'gql/queries';
import type { MonitoringStoresInput, MonitoringEventsInput } from 'types/installationMonitoring';
import { readMonitoringStores, readMonitoringEvents, validMonitoringInput } from 'utils/installationMonitoring';
import { boundedInstallationRead, installationAutomaticReadDue } from './useVisibleInstallationRefresh';
import { useCurrentStaffAccess } from './useCurrentStaffAccess';

type SupportCode = '' | 'authorization' | 'schema_unavailable' | 'upstream_unavailable' | 'timeout' | 'invalid_response' | 'request_failed';
const requestFailure = (error: unknown): SupportCode => {
  const value = error as { statusCode?: unknown; networkError?: { statusCode?: unknown }; graphQLErrors?: Array<{ extensions?: { code?: unknown } }> } | null;
  const status = value?.networkError?.statusCode ?? value?.statusCode;
  if (status === 401 || status === 403) return 'authorization';
  const codes = Array.isArray(value?.graphQLErrors) ? value!.graphQLErrors!.map(item => item?.extensions?.code) : [];
  if (codes.some(code => ['access-denied', 'FORBIDDEN', 'UNAUTHENTICATED'].includes(code as string))) return 'authorization';
  if (codes.includes('validation-failed')) return 'schema_unavailable';
  if (codes.includes('remote-schema-error')) return 'upstream_unavailable';
  return 'request_failed';
};

function useMonitoringRead<T>(allowed: boolean, actor: string, input: MonitoringStoresInput | MonitoringEventsInput,
  query: DocumentNode, field: string, decode: (value: unknown, input: any) => T, deny: () => void) {
  const client = useApolloClient(); const key = JSON.stringify(input), identity = `${actor}|${allowed}|${key}`;
  const boundary = useRef({ identity, client, generation: 0 });
  if (boundary.current.identity !== identity || boundary.current.client !== client)
    boundary.current = { identity, client, generation: boundary.current.generation + 1 };
  const scope = `${identity}|${boundary.current.generation}`;
  const active = useRef({ scope, client }); active.current = { scope, client };
  const mounted = useRef(false), attempted = useRef<number | null>(null);
  const pending = useRef<{ controller: AbortController; promise: Promise<void> } | null>(null);
  const [state, setState] = useState<{ scope: string; client: typeof client; data?: T; loading: boolean; error: string; supportCode: SupportCode }>({ scope, client, loading: false, error: '', supportCode: '' });
  const refresh = useCallback(() => {
    if (!allowed || !mounted.current || active.current.scope !== scope || active.current.client !== client) return Promise.resolve();
    if (pending.current) return pending.current.promise;
    const owner = { controller: new AbortController(), promise: Promise.resolve() };
    pending.current = owner; attempted.current = Date.now();
    const current = () => mounted.current && active.current.scope === scope && active.current.client === client && pending.current === owner;
    setState(before => ({ ...before, scope, client, data: before.scope === scope && before.client === client ? before.data : undefined, loading: true, error: '', supportCode: '' }));
    owner.promise = (async () => {
      let decoding = false;
      try {
        const result = await boundedInstallationRead(client.query({ query, variables: { input: JSON.parse(key) }, fetchPolicy: 'no-cache',
          context: { queryDeduplication: false, fetchOptions: { signal: owner.controller.signal } } }), owner.controller);
        if (result.errors?.length) throw Object.assign(new Error('Unconfirmed monitoring read'), { graphQLErrors: result.errors });
        decoding = true;
        const data = decode(result.data?.[field], JSON.parse(key));
        if (current()) setState({ scope, client, data, loading: false, error: '', supportCode: '' });
      } catch (error) {
        if (current()) {
          const supportCode = owner.controller.signal.aborted ? 'timeout' : decoding ? 'invalid_response' : requestFailure(error);
          if (supportCode === 'authorization') {
            setState({ scope, client, loading: false, error: '', supportCode });deny();
          } else setState(before => ({ ...before, loading: false, error: 'Monitoring could not be refreshed. Try again.', supportCode }));
        }
      } finally { if (pending.current === owner) pending.current = null; }
    })();
    return owner.promise;
  }, [allowed, client, decode, deny, field, key, query, scope]);
  useEffect(() => {
    mounted.current = true; attempted.current = null;
    setState({ scope, client, loading: false, error: '', supportCode: '' });
    const check = () => { if (document.visibilityState !== 'hidden' && installationAutomaticReadDue(attempted.current)) void refresh(); };
    if (allowed) check();
    const timer = allowed ? window.setInterval(check, 60000) : undefined;
    if (allowed) { window.addEventListener('focus', check); document.addEventListener('visibilitychange', check); }
    return () => {
      mounted.current = false; window.clearInterval(timer); window.removeEventListener('focus', check); document.removeEventListener('visibilitychange', check);
      pending.current?.controller.abort(); pending.current = null;
    };
  }, [allowed, client, refresh, scope]);
  const valid = allowed && state.scope === scope && state.client === client;
  return { data: valid ? state.data : undefined, loading: valid && state.loading, error: valid ? state.error : '', supportCode: valid ? state.supportCode : '', refresh };
}

export function useInstallationMonitoring(storesInput: MonitoringStoresInput, eventsInput: MonitoringEventsInput) {
  const access = useCurrentStaffAccess(), client = useApolloClient();
  const boundary = useRef({ key: access.key, client, generation: 0 });
  if (boundary.current.key !== access.key || boundary.current.client !== client)
    boundary.current = { key: access.key, client, generation: boundary.current.generation + 1 };
  const generation = boundary.current.generation, actor = `${access.actor}|${generation}`;
  const [deniedGeneration, setDeniedGeneration] = useState<number | null>(null);
  const deny = useCallback(() => setDeniedGeneration(generation), [generation]);
  const allowed = access.allowed && deniedGeneration !== generation;
  const storesAllowed = allowed && validMonitoringInput(storesInput), eventsAllowed = allowed && validMonitoringInput(eventsInput, true);
  const stores = useMonitoringRead(storesAllowed, actor, storesInput, GET_INSTALLATION_MONITORING_STORES, 'installationMonitoringStores', readMonitoringStores, deny);
  const events = useMonitoringRead(eventsAllowed, actor, eventsInput, GET_INSTALLATION_MONITORING_EVENTS, 'installationMonitoringEvents', readMonitoringEvents, deny);
  return { allowed, checking: access.checking, stores, events, refresh: () => Promise.all([stores.refresh(), events.refresh()]) };
}
